import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Pagination } from 'swiper/core'
import { createUseStyles } from 'react-jss'
import { Typography } from '../../ui/Typography/Typography'
import { colors } from '../../../theme/palette'
import { Dropdown } from '../../ui/Dropdown'
import { Case } from '../../../models/Case'

// Import Swiper styles
import 'swiper/swiper-bundle.css'
import {
    ChevronLeft,
    ChevronRight,
    IkonAfvissag,
    IkonSammenlaeg,
    ThreeDots,
} from '../../../assets/icons'
import { TakeCase } from './TakeCase'
import {
    casesToConfirmAtom,
    fetchCasesAtom,
    isFetchingCasesAtom,
    openCasesAtom,
} from '../../../store/filter/filterCasesAtom'
import { AttachToAnotherCase } from './AttachToAnotherCase'
import { Button } from '../../ui/Button/Button'
import { L } from '../../providers/Lokalise'
import { getCaseStatusColorCode } from '../../../utils/utils'
import { RejectCase } from './RejectCase'
import { formatDate } from '../../../utils/format'
import { Tooltip } from '../../ui/Tooltip/Tooltip'
import { profileAtom } from '../../../api/profile/profile'
import breakpoints from '../../../theme/breakpoints'

// Initialize Swiper modules
SwiperCore.use([Navigation, Pagination])

// Add keyframes animation
const style = document.createElement('style')
style.textContent = `
    @keyframes shimmer {
        0% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
`
document.head.appendChild(style)

const useStyles = createUseStyles({
    dropdownTrigger: {
        display: 'block',
        fontSize: '0.875rem',
        padding: '.5rem .5rem',
        borderRadius: '0 0 8px 0',
        border: 'none',
        margin: '0',
        borderLeft: `1px solid ${colors.borderColorLight}`,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: colors.green500,
            color: colors.white,
            '& svg': {
                fill: colors.white,
            },
        },
    },
    pagination: {
        display: 'none',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            display: 'block',
        },
    },
})

const skeletonStyle = {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        transform: 'translateX(-100%)',
        backgroundImage: `linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0,
            rgba(255, 255, 255, 0.2) 20%,
            rgba(255, 255, 255, 0.5) 60%,
            rgba(255, 255, 255, 0)
        )`,
        animation: 'shimmer 2s infinite',
    },
}

export interface CarouselProps {
    onTakeCaseClick?: (caseId: string) => void
    showTestCases?: boolean
}

export interface CarouselItemProps {
    currentCase: Case
}

const DropdownComponent = ({
    currentCase,
}: {
    currentCase: Case
}): JSX.Element => {
    const [attachCaseOpen, setAttachCaseOpen] = useState(false)
    const [rejectCaseOpen, setRejectCaseOpen] = useState(false)
    const [, fetchCases] = useAtom(fetchCasesAtom)
    const [authProfile] = useAtom(profileAtom)
    const [openCases] = useAtom(openCasesAtom)
    const classes = useStyles()

    return (
        <>
            <Dropdown
                align="right"
                trigger={
                    <Tooltip
                        content={L('LØN_KARRUSSEL_FLERE_HANDLINGER_TOOLTIP')}
                    >
                        <div className={classes.dropdownTrigger}>
                            <ThreeDots width="22px" />
                        </div>
                    </Tooltip>
                }
                items={[
                    ...(currentCase.allowedUpdateActionList.includes(
                        'SammenfletMedAndenSag'
                    )
                        ? [
                              {
                                  id: 'Attach',
                                  label: L(
                                      'LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_LABEL'
                                  ),
                                  onClick: () =>
                                      setAttachCaseOpen(!attachCaseOpen),
                                  icon: <IkonSammenlaeg />,
                                  disabled: authProfile.isImitator,
                              },
                          ]
                        : []),
                    ...(currentCase.allowedUpdateActionList.includes('Afvis')
                        ? [
                              {
                                  id: 'delete',
                                  label: L('LØN_OVERSIGT_AFVIS_SAG_LABEL'),
                                  color: colors.red600,
                                  icon: <IkonAfvissag fill={colors.red600} />,
                                  onClick: () => setRejectCaseOpen(true),
                                  disabled: authProfile.isImitator,
                              },
                          ]
                        : []),
                ]}
                style={{
                    fontSize: '0.875rem',
                }}
            />
            {attachCaseOpen && (
                <AttachToAnotherCase
                    currentCase={currentCase}
                    onSuccess={() => {
                        setAttachCaseOpen(false)
                        fetchCases()
                    }}
                    onCancel={() => {
                        setAttachCaseOpen(false)
                    }}
                    openCases={openCases}
                />
            )}
            {rejectCaseOpen && (
                <RejectCase
                    caseId={currentCase.caseId}
                    caseRegistrationDate={currentCase.caseRegistrationDate}
                    open={!!rejectCaseOpen}
                    handleClose={() => setRejectCaseOpen(false)}
                    onSuccess={() => {
                        setRejectCaseOpen(false)
                        fetchCases()
                    }}
                />
            )}
        </>
    )
}

const CarouselItem: React.FC<CarouselItemProps> = ({
    currentCase,
}): JSX.Element => {
    const navigate = useNavigate()
    const [, fetchCases] = useAtom(fetchCasesAtom)

    return (
        <>
            <div
                style={{
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    border:
                        currentCase.caseStatus?.statusType ===
                        'FristOverskredet'
                            ? `1px solid ${colors.caseRed500}`
                            : 'none',
                }}
            >
                <Button
                    type="ghost"
                    onClick={() => {
                        navigate(
                            `/mine-lonindplaceringer/sag/${currentCase.caseId}`
                        )
                    }}
                    style={{
                        padding: '1rem',
                    }}
                >
                    <Typography
                        tag="p"
                        size="XS"
                        style={{ color: colors.green }}
                    >
                        {currentCase.newlyHired?.name}
                    </Typography>
                    <div>
                        <div style={{ height: '20px', padding: '0.2rem 0' }}>
                            {currentCase?.caseStatus?.visStatus ? (
                                <Typography
                                    tag="p"
                                    size="XXXXS"
                                    style={{
                                        color: getCaseStatusColorCode(
                                            currentCase.caseStatus?.statusType
                                        ).color,
                                    }}
                                >
                                    {currentCase.caseStatus.statusNavn}
                                </Typography>
                            ) : null}
                        </div>
                        <Typography
                            tag="p"
                            size="XXXXS"
                            style={{ color: colors.gray800 }}
                        >
                            {`${L(
                                'LØN_KARRUSEL_MODTAGET_DATO_LABEL'
                            )} ${formatDate(
                                new Date(currentCase.receivedDate)
                            )}`}
                        </Typography>
                        <Typography tag="p" size="XXXS">
                            {currentCase.newlyHired?.workplace?.navn ?? ''}
                        </Typography>
                        <Typography tag="p" size="XXXS">
                            {currentCase.newlyHired?.position?.navn ?? ''}
                        </Typography>
                    </div>
                </Button>
                <div
                    style={{
                        borderTop: `1px solid ${colors.gray}`,
                        display: 'flex',
                    }}
                >
                    {currentCase.allowedUpdateActionList.includes('Accept') && (
                        <TakeCase
                            currentCase={currentCase}
                            onSuccess={() => {
                                fetchCases()
                            }}
                        />
                    )}
                    <DropdownComponent currentCase={currentCase} />
                </div>
            </div>
        </>
    )
}

const CarouselItemSkeleton: React.FC = (): JSX.Element => {
    return (
        <div
            style={{
                backgroundColor: colors.white,
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            }}
        >
            <div
                style={{
                    padding: '1rem',
                }}
            >
                <div
                    style={{
                        width: '60%',
                        height: '16px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        marginBottom: '0.5rem',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
                <div>
                    <div style={{ height: '20px', padding: '0.2rem 0' }}>
                        <div
                            style={{
                                width: '40%',
                                height: '12px',
                                backgroundColor: colors.gray,
                                borderRadius: '4px',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                    transform: 'translateX(-100%)',
                                    backgroundImage: `linear-gradient(
                                        90deg,
                                        rgba(255, 255, 255, 0) 0,
                                        rgba(255, 255, 255, 0.2) 20%,
                                        rgba(255, 255, 255, 0.5) 60%,
                                        rgba(255, 255, 255, 0)
                                    )`,
                                    animation: 'shimmer 1.2s infinite',
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            width: '70%',
                            height: '12px',
                            backgroundColor: colors.gray,
                            borderRadius: '4px',
                            marginBottom: '0.5rem',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                                transform: 'translateX(-100%)',
                                backgroundImage: `linear-gradient(
                                    90deg,
                                    rgba(255, 255, 255, 0) 0,
                                    rgba(255, 255, 255, 0.2) 20%,
                                    rgba(255, 255, 255, 0.5) 60%,
                                    rgba(255, 255, 255, 0)
                                )`,
                                animation: 'shimmer 1.2s infinite',
                            }}
                        />
                    </div>
                    <div
                        style={{
                            width: '80%',
                            height: '12px',
                            backgroundColor: colors.gray,
                            borderRadius: '4px',
                            marginBottom: '0.5rem',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                                transform: 'translateX(-100%)',
                                backgroundImage: `linear-gradient(
                                    90deg,
                                    rgba(255, 255, 255, 0) 0,
                                    rgba(255, 255, 255, 0.2) 20%,
                                    rgba(255, 255, 255, 0.5) 60%,
                                    rgba(255, 255, 255, 0)
                                )`,
                                animation: 'shimmer 1.2s infinite',
                            }}
                        />
                    </div>
                    <div
                        style={{
                            width: '90%',
                            height: '12px',
                            backgroundColor: colors.gray,
                            borderRadius: '4px',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                                transform: 'translateX(-100%)',
                                backgroundImage: `linear-gradient(
                                    90deg,
                                    rgba(255, 255, 255, 0) 0,
                                    rgba(255, 255, 255, 0.2) 20%,
                                    rgba(255, 255, 255, 0.5) 60%,
                                    rgba(255, 255, 255, 0)
                                )`,
                                animation: 'shimmer 1.2s infinite',
                            }}
                        />
                    </div>
                </div>
            </div>
            <div
                style={{
                    borderTop: `1px solid ${colors.gray}`,
                    display: 'flex',
                }}
            >
                <div
                    style={{
                        flex: 1,
                        height: '40px',
                        backgroundColor: colors.gray,
                        borderRadius: '0 0 0 8px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: colors.gray,
                        borderRadius: '0 0 8px 0',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

const Carousel: React.FC<CarouselProps> = ({ showTestCases }): JSX.Element => {
    const [casesToConfirm] = useAtom(casesToConfirmAtom)
    const [isLoading] = useAtom(isFetchingCasesAtom)
    const classes = useStyles()

    if (
        casesToConfirm.filter(
            (caseItem) => showTestCases || caseItem.caseNumber?.startsWith('LI')
        ).length === 0 &&
        !isLoading
    ) {
        return (
            <div
                style={{
                    paddingTop: '1rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <Typography size="S">
                    {L(
                        'LØN_KARRUSSEL_INGEN_SAGER_DER_AFVENTER_BEKRÆFTES_LABEL'
                    )}
                </Typography>
            </div>
        )
    }

    return (
        <div
            style={{
                width: 'calc(100% - 4rem)',
                margin: '0 auto',
                position: 'relative',
            }}
        >
            <Swiper
                breakpoints={{
                    320: {
                        slidesPerView: 1,
                    },
                    640: {
                        slidesPerView: 2,
                        slidesPerGroup: 2,
                    },
                    768: {
                        slidesPerView: 3,
                        slidesPerGroup: 3,
                    },
                    1024: {
                        slidesPerView: 4,
                        slidesPerGroup: 4,
                    },
                }}
                pagination={{
                    el: '.pagination',
                    clickable: true,
                }}
                spaceBetween={10}
                navigation={{
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                }}
                style={{
                    width: '100%',
                    position: 'initial',
                    overflowX: 'clip',
                    overflowY: 'visible',
                }}
            >
                {!isLoading &&
                    casesToConfirm
                        .filter(
                            (caseItem) =>
                                showTestCases ||
                                caseItem.caseNumber?.startsWith('LI')
                        )
                        .map((currentCase) => {
                            return (
                                <SwiperSlide key={currentCase.caseId}>
                                    <CarouselItem currentCase={currentCase} />
                                </SwiperSlide>
                            )
                        })}
                {isLoading &&
                    Array.from({ length: 4 }).map((_) => (
                        <SwiperSlide
                            key={`carousel-skeleton-${Math.random()
                                .toString(36)
                                .substr(2, 9)}`}
                        >
                            <CarouselItemSkeleton />
                        </SwiperSlide>
                    ))}
                <div
                    className="swiper-button-container"
                    style={{
                        position: 'absolute',
                        top: 'calc(50% - 1.25rem)',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div className="swiper-button-next">
                        <ChevronLeft />
                    </div>
                    <div className="swiper-button-prev">
                        <ChevronRight />
                    </div>
                </div>
                <div
                    className={`${classes.pagination} pagination`}
                    style={{ textAlign: 'center', marginTop: '1rem' }}
                />
            </Swiper>
        </div>
    )
}

export default Carousel
